import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useParams,
  Navigate,
  Outlet,
} from "react-router-dom";

import Header from "./components/website/header/Header";
import { useEid55Active } from "./config/campaigns";
import EidCollectionPage from "./pages/EidCollection55/EidCollectionPage";
import Footer from "./components/website/footer/Footer";
import HomePage from "./pages/home/HomePage";
import ServicesPage from "./pages/services/ServicesPage";
import ContactPage from "./pages/contact/ContactPage";
import AboutPage from "./pages/about/AboutPage";
import ServiceDetailsPage from "./pages/services/serviceDetails/ServiceDetailsPage";
import OurWorkPage from "./pages/achievement/OurWorkPage";
import OurWorkDetailsPage from "./pages/achievement/ourworkDetails/OurWorkDetailsPage";

import { useScrollToTop } from "./hooks/UseScrollTop";
import { useI18n } from "./languages/providers/I18nProvider";
import NotFoundPage from "./pages/notFound/Notfoundpage";

const SUPPORTED_LANGS = ["en", "ar"];

const LangWrapper = () => {
  const { lang } = useParams<{ lang: string }>();
  const { setLang } = useI18n();

  React.useEffect(() => {
    if (SUPPORTED_LANGS.includes(lang!)) {
      setLang(lang as "en" | "ar");
    }
  }, [lang]);

  if (!SUPPORTED_LANGS.includes(lang!)) {
    return <Navigate to="/en" replace />;
  }

  return <Outlet />;
};

const Layout = () => {
  useScrollToTop();
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  const eidActive = useEid55Active();
  const isHome =
    location.pathname === `/${lang}` || location.pathname === `/${lang}/`;

  return (
    <>
      {!isHome && <Header variant={eidActive ? "eid55" : "default"} />}
      <Outlet />
      <Footer />
    </>
  );
};

/** Old Eid URL → permanent one (keeps any links already shared). */
const OldEidRedirect = () => {
  const { lang } = useParams<{ lang: string }>();
  const { search } = useLocation(); // keep ?design=3 deep links working
  return <Navigate to={`/${lang}/eid-al-etihad${search}`} replace />;
};

const App = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/" element={<Navigate to="/en" replace />} />

      <Route path="/:lang" element={<LangWrapper />}>
        <Route element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="services/:slug" element={<ServiceDetailsPage />} />
          <Route path="ourwork" element={<OurWorkPage />} />
          <Route path="ourwork/:id" element={<OurWorkDetailsPage />} />

          {/* Evergreen Eid page: same URL every year, so ranking builds up */}
          <Route path="eid-al-etihad" element={<EidCollectionPage />} />
          <Route path="ourwork/eid-al-etihad-55" element={<OldEidRedirect />} />

          <Route path="about" element={<AboutPage />} />
          <Route path="contact" element={<ContactPage />} />

          {/* Real 404 instead of redirecting everything to /en */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  </BrowserRouter>
);

export default App;
